import { Fragment, createElement, useCallback, useMemo } from 'react';
import { I18nContext } from './I18n.context.ts';
import { getErrorMessageKey, interpolate } from './I18n.helpers.ts';
import type {
  TI18nContextValue,
  TI18nProviderProps,
  TMessageKey,
  TInterpolations,
  TReasonKeys,
  TTranslate,
} from './I18n.types.ts';

export const I18nProvider = ({
  locale,
  messages,
  children,
}: TI18nProviderProps) => {
  const translate = useCallback(
    (key: TMessageKey, interpolations?: TInterpolations) => {
      const message = interpolate(messages[key] ?? key, interpolations);

      return typeof message === 'string'
        ? message
        : createElement(Fragment, null, ...message);
    },
    [messages],
  ) as TTranslate;

  const translateError = useCallback(
    (error: unknown, reasonKeys?: TReasonKeys) => {
      const key = getErrorMessageKey(error, reasonKeys);

      return translate(key);
    },
    [translate],
  );

  const value = useMemo<TI18nContextValue>(
    () => ({ locale, translate, translateError }),
    [locale, translate, translateError],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};
